import { useEffect, useMemo, useState } from "react";
import { Search, SearchX, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { TermCard } from "@/components/TermCard";
import { AlphabetBar } from "@/components/AlphabetBar";
import { subjectColor } from "@/components/SubjectBadge";
import { WordOfTheDay } from "@/components/WordOfTheDay";

export default function Home() {
  const [terms, setTerms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState(null);
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
    api.get("/terms").then((r) => setTerms(r.data)).finally(() => setLoading(false));
  }, []);

  const bySubject = useMemo(() => terms.filter((t) => !subject || t.subject === subject), [terms, subject]);
  const letters = useMemo(() => new Set(bySubject.map((t) => t.word[0]?.toUpperCase())), [bySubject]);
  const visible = bySubject.filter(
    (t) => (!letter || t.word[0]?.toUpperCase() === letter) && (!q || t.word.toLowerCase().includes(q.toLowerCase()) || t.definition.toLowerCase().includes(q.toLowerCase()))
  );

  const reset = () => { setQ(""); setSubject(null); setLetter(null); };

  return (
    <main className="mx-auto max-w-6xl px-5 pb-24 sm:px-8">
      <section className="grid items-center gap-10 py-12 md:grid-cols-[1.3fr_1fr] md:py-16">
        <div className="rise">
          <span className="ink-box-sm inline-flex items-center gap-2 rounded-full bg-[var(--sun)] px-3 py-1 text-xs font-extrabold uppercase tracking-widest">
            <Sparkles className="h-3.5 w-3.5" /> Parole, fumetti e mappe
          </span>
          <h1 className="font-display mt-5 text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            Le parole difficili, <span className="relative inline-block"><span className="relative z-10">spiegate facili.</span><span className="absolute bottom-1 left-0 -z-0 h-4 w-full -rotate-1 bg-[var(--coral)]/70" /></span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-neutral-700 md:text-lg">
            Cerca una parola di qualsiasi materia: trovi una definizione semplice, frasi di esempio, un fumetto e una mappa concettuale.
          </p>
          <div className="ink-box mt-8 flex items-center gap-3 rounded-2xl bg-white px-4 py-3">
            <Search className="h-5 w-5 shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca una parola… es. fotosintesi"
              data-testid="search-input"
              className="w-full bg-transparent text-base font-semibold outline-none placeholder:text-neutral-400"
            />
          </div>
        </div>
        <div className="relative hidden md:block">
          <div className="ink-box rotate-2 overflow-hidden rounded-3xl bg-white">
            <img src="https://images.pexels.com/photos/8342189/pexels-photo-8342189.jpeg?auto=compress&cs=tinysrgb&w=900" alt="Ragazzi che leggono fumetti" className="h-80 w-full object-cover" />
          </div>
          <div className="bubble font-comic absolute -left-8 -top-6 -rotate-6 px-5 py-3 text-2xl">CHE SIGNIFICA?!</div>
        </div>
      </section>

      <WordOfTheDay />

      <section className="space-y-5">
        <div className="flex flex-wrap gap-2" data-testid="subject-filter">
          <button onClick={() => setSubject(null)} data-testid="subject-filter-all" className={`ink-box-sm rounded-full px-4 py-1.5 text-sm font-extrabold transition-colors ${!subject ? "bg-[var(--ink)] text-white" : "bg-white"}`}>
            Tutte le materie
          </button>
          {subjects.map((s) => (
            <button
              key={s}
              onClick={() => { setSubject(s === subject ? null : s); setLetter(null); }}
              data-testid={`subject-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`ink-box-sm rounded-full px-4 py-1.5 text-sm font-extrabold transition-transform hover:-translate-y-0.5 ${subject === s ? "bg-[var(--ink)] text-white" : subjectColor(s)}`}
            >
              {s}
            </button>
          ))}
        </div>
        <AlphabetBar active={letter} available={letters} onSelect={setLetter} />
      </section>

      <p className="mt-8 text-sm font-bold text-neutral-600" data-testid="results-count">
        {loading ? "Caricamento…" : `${visible.length} ${visible.length === 1 ? "parola" : "parole"}`}
      </p>

      {!loading && visible.length === 0 ? (
        <div className="ink-box mt-4 flex flex-col items-center rounded-3xl bg-white px-6 py-14 text-center" data-testid="empty-state">
          <SearchX className="h-10 w-10" />
          <p className="font-display mt-3 text-xl font-bold">Nessuna parola trovata</p>
          <button onClick={reset} data-testid="reset-filters-button" className="ink-box-sm ink-press mt-5 rounded-full bg-[var(--sun)] px-5 py-2 font-extrabold">Azzera i filtri</button>
        </div>
      ) : (
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="terms-grid">
          {visible.map((t, i) => <TermCard key={t.id} term={t} index={i} />)}
        </div>
      )}
    </main>
  );
}
